// src/components/pages/admin-dash/AdminOverview.jsx
import React, { useEffect, useMemo, useState } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import Spinner from "../../ui/Spinner";
import Badge from "../../ui/Badge";
import AdminStatCard from "./AdminStatCard";
import axiosInstance from "../../../utils/AxiosInstance";

/**
 * Admin overview.
 *
 * KPI tiles, queues, recent activity, quick actions. All endpoints
 * marked TODO are placeholders for backend wiring — they degrade
 * gracefully when missing (the tile shows "—" and the queue shows the
 * empty-state).
 *
 * Backend endpoints expected (suggested):
 *   GET /admin/metrics/summary
 *       → { totalUsers, activeSubscriptions, revenue30d, picksToday,
 *           usersDelta30d, revenueDelta30d, picksDeltaWeekly }
 *   GET /admin/payment/list-by-filter?statusPayment=PROCESSING&limit=5
 *   GET /admin/user/list-all?orderBy=createdAt&dir=desc&limit=5
 *       (we client-side slice list-all if the backend doesn't support sort)
 *
 * Props:
 *   onNavigate(viewId)   navigate to another admin section (used by quick
 *                        actions and the "see all" buttons)
 */
const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

const fmtNum = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US").format(n)
    : "—";

const relTime = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now  = Date.now();
  if (!Number.isFinite(then)) return "—";
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60000);
  if (m < 1)    return "just now";
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)   return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function AdminOverview({ onNavigate, onShowToast }) {
  const [metrics, setMetrics]     = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // ----- Metrics (TODO: backend endpoint) -----
        const metricsP = axiosInstance
          .get("/admin/metrics/summary")
          .then((r) => r.data)
          .catch(() => null);

        // ----- Pending payments (existing endpoint, with filter) -----
        const pendingP = axiosInstance
          .get("/admin/payment/list-by-filter", {
            params: { statusPayment: "PROCESSING" },
          })
          .then((r) => Array.isArray(r.data) ? r.data : [])
          .catch(() => []);

        // ----- Recent users (existing list-all, client-side sort) -----
        const usersP = axiosInstance
          .get("/admin/user/list-all")
          .then((r) => Array.isArray(r.data) ? r.data : [])
          .catch(() => []);

        const [m, pp, uu] = await Promise.all([metricsP, pendingP, usersP]);
        if (cancelled) return;

        setMetrics(m);
        setPendingPayments(pp.slice(0, 5));
        const sorted = [...uu].sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
        );
        setRecentUsers(sorted.slice(0, 5));
      } catch {
        if (!cancelled) onShowToast?.("Couldn't load overview data.", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [onShowToast]);

  /* --------------------- Derived display values --------------------- */
  const tiles = useMemo(
    () => [
      {
        label: "Total users",
        value: fmtNum(metrics?.totalUsers),
        icon: "users",
        helper: metrics?.usersDelta30d != null
          ? `+${fmtNum(metrics.usersDelta30d)} in 30d`
          : null,
      },
      {
        label: "Active subscriptions",
        value: fmtNum(metrics?.activeSubscriptions),
        icon: "credit-card",
        tone: "gold",
      },
      {
        label: "Revenue (30d)",
        value: fmtUsd(metrics?.revenue30d),
        icon: "trending-up",
        delta: metrics?.revenueDelta30d != null
          ? {
              value: `${metrics.revenueDelta30d >= 0 ? "+" : ""}${
                Math.round(metrics.revenueDelta30d * 100) / 100
              }%`,
              positive: metrics.revenueDelta30d >= 0,
            }
          : null,
      },
      {
        label: "Picks today",
        value: fmtNum(metrics?.picksToday),
        icon: "sparkles",
        helper: metrics?.picksDeltaWeekly != null
          ? `vs. last week`
          : null,
      },
    ],
    [metrics]
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <header>
        <h1
          className="font-semibold mb-1"
          style={{ fontSize: 26, color: "var(--txt)" }}
        >
          Overview
        </h1>
        <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>
          A snapshot of users, revenue, and what needs your attention.
        </p>
      </header>

      {/* KPI tiles */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <AdminStatCard
            key={t.label}
            label={t.label}
            value={t.value}
            icon={t.icon}
            tone={t.tone}
            delta={t.delta}
            helper={t.helper}
            loading={loading}
          />
        ))}
      </div>

      {/* Quick actions */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="lg-label">Quick actions</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button icon="plus"
            size="sm"
            variant="ghost"
            onClick={() => onNavigate("createDraw")}>New draw
          </Button>
          <Button icon="coin"
            size="sm"
            variant="ghost"
            onClick={() => onNavigate("bulkCredits")}>Grant credits
          </Button>
          <Button icon="credit-card"
            size="sm"
            variant="ghost"
            onClick={() => onNavigate("listPaymentsByFilter")}>Review payments
          </Button>
          <Button icon="mail"
            size="sm"
            variant="ghost"
            onClick={() => onNavigate("messages")}>Contact inbox
          </Button>
        </div>
      </Card>

      {/* Two-column queue & recent */}
      <div className="grid gap-3 lg:grid-cols-2">
        {/* Pending payments */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="lg-label mb-0.5">Pending payments</div>
              <div className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                Manual review needed
              </div>
            </div>
            {pendingPayments.length > 0 && (
              <button
                type="button"
                onClick={() => onNavigate("listPaymentsByFilter")}
                className="inline-flex items-center gap-1 text-[12px] font-medium hover:underline whitespace-nowrap"
                style={{ color: "var(--gold)" }}
              >
                View all <Icon name="arrow-right" size={12} />
              </button>
            )}
          </div>

          {loading ? (
            <LoadingRow />
          ) : pendingPayments.length === 0 ? (
            <EmptyRow
              icon="circle-check"
              message="Nothing pending."
              tone="gold"
            />
          ) : (
            <div className="space-y-1">
              {pendingPayments.map((p, idx, arr) => (
                <PaymentQueueRow
                  key={p.id}
                  p={p}
                  isLast={idx === arr.length - 1}
                  onClick={() => onNavigate("updatePayment", { paymentId: p.id })}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Recent signups */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="lg-label mb-0.5">Recent signups</div>
              <div className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                Newest accounts
              </div>
            </div>
            <button
              type="button"
              onClick={() => onNavigate("viewAllUsers")}
              className="inline-flex items-center gap-1 text-[12px] font-medium hover:underline whitespace-nowrap"
              style={{ color: "var(--gold)" }}
            >
              View all <Icon name="arrow-right" size={12} />
            </button>
          </div>

          {loading ? (
            <LoadingRow />
          ) : recentUsers.length === 0 ? (
            <EmptyRow icon="users" message="No users yet." />
          ) : (
            <div className="space-y-1">
              {recentUsers.map((u, idx, arr) => (
                <UserQueueRow
                  key={u.id}
                  u={u}
                  isLast={idx === arr.length - 1}
                  onClick={() => onNavigate("viewAllUsers", { focusUserId: u.id })}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* System health placeholder (TODO: backend) */}
      <Card variant="soft">
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 inline-flex items-center justify-center rounded-xl"
            style={{
              width: 40,
              height: 40,
              background: "var(--gold-dim)",
              color: "var(--gold)",
              border: "1px solid var(--gold-border)",
            }}
          >
            <Icon name="shield-check" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[14px] mb-1" style={{ color: "var(--txt)" }}>
              System status
            </div>
            <div className="text-[12px]" style={{ color: "var(--txt-2)" }}>
              All services nominal. Last cron run:{" "}
              <span style={{ color: "var(--txt)" }}>
                {metrics?.lastCronRunAt ? relTime(metrics.lastCronRunAt) : "—"}
              </span>
              {" · "}
              Queue depth:{" "}
              <span style={{ color: "var(--txt)" }}>
                {metrics?.queueDepth ?? "—"}
              </span>
            </div>
          </div>
          <Badge variant={metrics?.systemHealthy === false ? "red" : "teal"}>
            {metrics?.systemHealthy === false ? "Degraded" : "Healthy"}
          </Badge>
        </div>
      </Card>
    </div>
  );
}

/* ============================================================
   Sub-rows
   ============================================================ */

function PaymentQueueRow({ p, isLast, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between py-2.5 px-1 -mx-1 rounded-md transition-colors text-left"
      style={{
        borderBottom: isLast ? "none" : "1px solid var(--border)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surf-2)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div className="min-w-0">
        <div
          className="font-medium text-[13px] truncate"
          style={{ color: "var(--txt)" }}
        >
          {p.user?.name || `User #${p.user?.id || "?"}`}
        </div>
        <div className="text-[11px]" style={{ color: "var(--txt-2)" }}>
          {p.creditPlan || p.subscription?.plan || "Subscription"} ·{" "}
          {p.paymentMethod || "—"} · #{p.id}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span
          className="font-medium tabular-nums text-[13px]"
          style={{ color: "var(--txt)" }}
        >
          {fmtUsd(p.amount)}
        </span>
        <Badge variant="gold">Pending</Badge>
      </div>
    </button>
  );
}

function UserQueueRow({ u, isLast, onClick }) {
  const initials =
    (u.name || u.email || "?")
      .split(/\s+/)
      .map((s) => s[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join("") || "?";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 py-2.5 px-1 -mx-1 rounded-md transition-colors text-left"
      style={{
        borderBottom: isLast ? "none" : "1px solid var(--border)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surf-2)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div
        className="shrink-0 inline-flex items-center justify-center rounded-full font-semibold text-[11px]"
        style={{
          width: 32,
          height: 32,
          background: "var(--gold-dim)",
          color: "var(--gold)",
          border: "1px solid var(--gold-border)",
        }}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="font-medium text-[13px] truncate"
          style={{ color: "var(--txt)" }}
        >
          {u.name || "—"}
        </div>
        <div className="text-[11px] truncate" style={{ color: "var(--txt-2)" }}>
          {u.email || `User #${u.id}`}
        </div>
      </div>
      <div
        className="shrink-0 text-[11px] tabular-nums"
        style={{ color: "var(--txt-3)" }}
      >
        {u.createdAt ? relTime(u.createdAt) : `#${u.id}`}
      </div>
    </button>
  );
}

function LoadingRow() {
  return (
    <div
      className="flex items-center gap-2 py-3"
      style={{ color: "var(--txt-3)" }}
    >
      <Spinner size={14} />
      <span className="text-[12px]">Loading…</span>
    </div>
  );
}

function EmptyRow({ icon = "database", message, tone }) {
  return (
    <div className="text-center py-5">
      <Icon
        name={icon}
        size={26}
        style={{ color: tone === "gold" ? "var(--gold)" : "var(--txt-3)" }}
      />
      <p className="text-[13px] mt-2" style={{ color: "var(--txt-2)" }}>
        {message}
      </p>
    </div>
  );
}
